import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { entityMessages } from 'src/features/entity/utils/messages';
import { EntityFormSchemaValues } from 'src/features/entity/formSchema';
import { Separator } from 'src/registry/ui/Separator';
import { Popover } from '@radix-ui/react-popover';
import * as React from 'react';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { useListEntities } from 'src/features/entity/api/useListEntities';
import { ENTITY_TYPE } from 'src/features/entity/types';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'src/registry/ui/Command';
import { cn } from 'src/utils/shadcn';
import { useParking } from 'src/providers/ParkingProvider';

type EntityFormProps = {
    form: UseFormReturn<EntityFormSchemaValues>;
    onSubmit: (values: EntityFormSchemaValues) => void;
};

function EntityForm({ onSubmit, form }: EntityFormProps) {
    const { formatMessage } = useIntl();
    const { parent } = form.getValues();
    const { parkingId } = useParking();

    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [value, setValue] = React.useState(parent ?? '');

    const { data: groupsData } = useListEntities({
        parkingId,
        type: ENTITY_TYPE.ORGANISATION
    });

    const searchText = React.useMemo(() => {
        if (!groupsData) return null;

        return groupsData.results.find((entity) => entity.id === value)?.name ?? null;
    }, [groupsData, value]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(entityMessages.name)}
            />
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="phone"
                pattern={/^(?:\+?[0-9]+)?$/}
                type='tel'
                required={false}
                label={formatMessage(entityMessages.phone)}
            />
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="email"
                type="email"
                required={false}
                label={formatMessage(entityMessages.email)}
            />
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="description"
                required={false}
                label={formatMessage(entityMessages.description)}
            />
            <InputFormField<EntityFormSchemaValues>
                form={form}
                name="identifier"
                required={false}
                label={formatMessage(entityMessages.identifier)}
            />

            <Separator/>

            <Popover open={open} onOpenChange={setOpen}>
                <FormItem variant="dialog">
                    <FormLabel variant="dialog">{formatMessage(entityMessages.group)}</FormLabel>
                    <FormControlStack>
                        <FormControl>
                            <PopoverTrigger asChild>
                                <Button
                                    className="h-auto flex-wrap"
                                    variant="dropdown"
                                    role="combobox"
                                    aria-expanded={open}
                                >
                                    <FontAwesomeIcon
                                        icon={ICON_MAP.DOWN}
                                        className="text-primary-700 my-1.5 mr-1 size-3 w-4 shrink-0"
                                    />
                                    {searchText ?? (
                                        <p className="text-gray-400">{formatMessage(entityMessages.group)}...</p>
                                    )}
                                </Button>
                            </PopoverTrigger>
                        </FormControl>
                        <FormMessage />
                    </FormControlStack>
                </FormItem>
                <PopoverContent
                    style={{ width: 'var(--radix-popover-trigger-width)' }}
                    align="start"
                    className="p-0 pb-12"
                >
                    <Command>
                        <CommandInput
                            value={search}
                            onValueChange={setSearch}
                            placeholder={formatMessage(entityMessages.searchGroup)}
                            maxLength={255}
                        />
                        <CommandList>
                            <CommandEmpty className="flex flex-col items-center justify-center p-1 px-4 py-8 text-center text-balance">
                                <p className="text-sm font-semibold">{formatMessage(membershipMessages.noEntities)}</p>
                                <p className="text-sm text-gray-500">
                                    {formatMessage(membershipMessages.noEntitiesFound)}
                                </p>
                            </CommandEmpty>
                            <CommandGroup className={cn('max-h-40 overflow-y-auto', !groupsData?.results?.length && 'hidden')}>
                                {groupsData?.results?.map((entity) => (
                                    <CommandItem
                                        key={entity.id}
                                        value={entity.id}
                                        keywords={[entity.name]}
                                        onSelect={() => {
                                            form.setValue('parent', entity.id, { shouldDirty: true });
                                            setValue(entity.id);
                                            setOpen(false);
                                        }}
                                    >
                                        {entity.name}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                            <CommandGroup
                                forceMount
                                className="absolute bottom-0 left-0 z-50 w-full rounded-b border bg-white"
                            >
                                <CommandItem
                                    onSelect={() => {
                                        form.setValue('parent', null, { shouldDirty: true });
                                        setValue('');
                                        setOpen(false);
                                    }}
                                    className="cursor-pointer"
                                >
                                    <FontAwesomeIcon
                                        icon={ICON_MAP.DELETE}
                                        className={cn('mr-1 size-3 w-4 shrink-0 text-red-500')}
                                    />
                                    {formatMessage(membershipMessages.clearSelection)}
                                </CommandItem>
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </Form>
    );
}

export default EntityForm;
